import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

/**
 * v5 — 40×40 circle icon button. Replaces the old rounded-rect icon
 * button everywhere. For labelled actions use <Button /> (pill).
 *
 * Variants: default | primary | outline | ghost | destructive
 * Sizes:    sm (32) | default (40) | lg (48) | fab (56 + mint glow)
 */
const iconButtonVariants = cva(
  "inline-flex items-center justify-center rounded-full border transition-all duration-200 ease-ac-out focus-visible:outline-none focus-visible:[box-shadow:var(--ac-glow-mint-sm)] disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-[hsl(var(--ac-dark-accent))] text-[hsl(var(--ac-text-light))] border-[hsl(var(--ac-dark-accent))] hover:bg-[hsl(var(--ac-mint-primary)/0.15)] hover:border-[hsl(var(--ac-mint-primary)/0.5)] hover:text-[hsl(var(--ac-mint-primary))]",
        primary:
          "bg-[hsl(var(--ac-mint-primary))] text-[hsl(var(--ac-text-dark))] border-[hsl(var(--ac-mint-primary))] [box-shadow:var(--ac-glow-mint-sm)] hover:bg-[hsl(var(--ac-mint-secondary))] hover:[box-shadow:var(--ac-glow-mint-md)]",
        outline:
          "bg-transparent text-[hsl(var(--ac-mint-primary))] border-[hsl(var(--ac-mint-primary))] hover:bg-[hsl(var(--ac-mint-primary)/0.12)]",
        ghost:
          "bg-transparent text-[hsl(var(--ac-text-light))] border-transparent hover:bg-[hsl(var(--ac-dark-accent))]",
        destructive:
          "bg-[hsl(var(--ac-danger)/0.15)] text-[hsl(var(--ac-danger))] border-[hsl(var(--ac-danger)/0.4)] hover:bg-[hsl(var(--ac-danger)/0.25)] hover:border-[hsl(var(--ac-danger)/0.6)]",
      },
      size: {
        sm: "w-8 h-8 text-xs",
        default: "w-10 h-10 text-sm",
        lg: "w-12 h-12 text-base",
        fab: "w-14 h-14 text-lg [box-shadow:var(--ac-glow-mint-md)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  asChild?: boolean
  /** Required for accessibility — icon buttons have no visible label */
  "aria-label": string
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(iconButtonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    )
  }
)
IconButton.displayName = "IconButton"

export { IconButton, iconButtonVariants }
